import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions } from '@ngrx/effects';
import { DeviceInput, DeviceTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { DetailView } from 'src/app/common-components/detail/detail-view';
import { DeviceRegisterListItem } from '../../device-register/models/device-register-results.model';
import { DeviceDetailSaveEvent } from '../models/device-detail-form-submit-event';
import { DeviceDetailView } from '../models/device-detail-view.model';
import { DevicePrivileges } from '../models/device-privileges';
import { DeviceDetailFormService } from '../services/device-detail-form.service';
import { BaseStationHandsetItemView } from './models/handset-item-view.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-device-detail-view',
    styleUrls: ['./device-detail-view.component.scss'],
    templateUrl: './device-detail-view.component.html',
    standalone: false
})
export class DeviceDetailViewComponent extends DetailView {
   @Input() get device(): DeviceDetailView | null {
      return this._device;
   }

   set device(value: DeviceDetailView | null) {
      this._device = value;
      if (value) {
         this.formService.updateDevice(value);
      }
   }
   canEdit: boolean = true;

   public DeviceTypes = DeviceTypes;

   baseStationHandsetSteps: BaseStationHandsetItemView[] = [];

   @Input() set devicePrivileges(privileges: DevicePrivileges) {
      this._devicePrivileges = privileges;
      if (!privileges?.canEdit) {
         this.form.disable();
      } else if (privileges.canEdit) {
         this.form.enable();
         this.form.get('macAddress')?.disable();
         this.form.get('deviceTypeId')?.disable();
      }
   }

   get devicePrivileges(): DevicePrivileges {
      return this._devicePrivileges;
   }

   get activeBaseStationHandsetSteps(): BaseStationHandsetItemView[] {
      return this.baseStationHandsetSteps.filter((v) => !v.isDeleted);
   }

   get allSipUsers(): any[] {
      const fromDevice = this.device?.phone?.sipUsersConnection?.items ?? [];
      const fromHandsets = (this.device?.baseStationHandsetsConnection ?? [])
         .map((h) => h.phone?.sipUsers ?? [])
         .reduce((acc, val) => acc.concat(val), []);
      return [...fromDevice, ...fromHandsets];
   }

   get distinctInternalIps(): string[] {
      return [
         ...new Set(
            this.allSipUsers
               .map((u) => u.internalIps?.split(',').map((ip) => ip.trim()) || [])
               .reduce<string[]>((acc, val) => acc.concat(val), [])
               .filter((ip) => !!ip)
         ),
      ];
   }

   get distinctExternalIps(): string[] {
      return [
         ...new Set(
            this.allSipUsers
               .map((u) => u.externalIps?.split(',').map((ip) => ip.trim()) || [])
               .reduce<string[]>((acc, val) => acc.concat(val), [])
               .filter((ip) => !!ip)
         ),
      ];
   }

   get distinctUserAgents(): string[] {
      return [
         ...new Set(
            this.allSipUsers
               .map((u) => u.userAgents?.split(',').map((agent) => agent.trim()) || [])
               .reduce<string[]>((acc, val) => acc.concat(val), [])
               .filter((agent) => !!agent)
         ),
      ];
   }

   @Input() callState: CallState;

   @Output() formSaveCanceled = new EventEmitter<void>();

   @Output() formSubmitted = new EventEmitter<DeviceDetailSaveEvent>();

   @Output() deleteDevice = new EventEmitter<DeviceDetailSaveEvent>();

   @Output() deviceReleased = new EventEmitter<DeviceDetailSaveEvent>();

   @Output() releaseToAccount = new EventEmitter<DeviceRegisterListItem>();

   @Output() resetHackedAndFraud: EventEmitter<DeviceDetailView>;

   @Output() rebootPhone = new EventEmitter<number>();

   @Output() resetPhoneZtp = new EventEmitter<number>();

   form: UntypedFormGroup;

   deviceTypes = Object.values(DeviceTypes);

   private _device: DeviceDetailView | null;

   private _handsetDevices: any | null;

   private _devicePrivileges: DevicePrivileges;

   private destroyed$ = new Subject<void>();

   constructor(
      public formService: DeviceDetailFormService,
      private actions$: Actions,
      private changeDetectorRef: ChangeDetectorRef
   ) {
      super();
      this.form = this.formService.form;
      this.resetHackedAndFraud = new EventEmitter<DeviceDetailView>();
   }

   onSubmit(): void {
      const submitEvent = this.getFormSubmittedEvent();
      if (submitEvent) {
         this.formSubmitted.emit(submitEvent);
      }
   }

   onCancel(): void {
      this.formSaveCanceled.emit();
   }

   onDeleteClick(): void {
      if (this.device) {
         this.deleteDevice.emit({
            device: this.device,
            deviceFields: [],
         });
      }
   }

   onReleaseDeviceClick(): void {
      const releaseEventModel: DeviceDetailSaveEvent = {
         device: {
            accountId: null,
            deviceId: this.device?.deviceId ?? null,
         },
         deviceFields: ['accountId'],
      };

      this.deviceReleased.emit(releaseEventModel);
   }

   onReleaseToAccountClick(device: DeviceRegisterListItem): void {
      this.releaseToAccount.emit(device);
   }

   rebootPhoneClick(device: DeviceDetailView | null) {
      if (device) this.rebootPhone.emit(device.deviceId);
   }

   resetPhoneZtpClick(device: DeviceDetailView) {
      this.resetPhoneZtp.emit(device.deviceId);
   }

   resetHackedAndFraudDevice(device: DeviceDetailView) {
      this.resetHackedAndFraud.emit(device);
   }

   private getFormSubmittedEvent(): DeviceDetailSaveEvent | null {
      if (!this.formService.form.dirty || !this.device) {
         return null;
      }
      const device = this.formService.getValue(this.device.deviceId);
      let deviceFields: (keyof DeviceInput)[] = [];
      deviceFields = this.formService.addFieldForDirtyControl(deviceFields, 'account', 'accountId');
      deviceFields = this.formService.addFieldForDirtyControl(deviceFields, 'macAddress');
      deviceFields = this.formService.addFieldForDirtyControl(deviceFields, 'deviceTypeId');
      deviceFields = this.formService.addFieldForDirtyControl(deviceFields, 'lastFirmwareReported');
      deviceFields = this.formService.addFieldForDirtyControl(deviceFields, 'lastProvisionedDate');
      deviceFields = this.formService.addFieldForDirtyControl(deviceFields, 'ztStatus');
      deviceFields = this.formService.addFieldForDirtyControl(deviceFields, 'notes');
      deviceFields = this.formService.addFieldForDirtyControl(deviceFields, 'deviceId');
      deviceFields = this.formService.addFieldForDirtyControl(deviceFields, 'description');
      deviceFields = this.formService.addFieldForDirtyControl(deviceFields, 'baseStationHandsets');
      return { device, deviceFields };
   }
}
